import { useEffect, useState } from "react";
import { Trash3FillIcon } from "./icons";
import Toast from "./Toast";
import DomainChecks from "./DomainChecks";
import ClubContactFields, { contactDraft } from "./ClubContactFields";
import {
  addClubManager,
  adoptClubEvents,
  createAdminClub,
  listAdminClubs,
  removeClubManager,
  updateAdminClub,
  type ClubContact,
  type ClubRole,
  type ClubRow,
} from "../lib/api";

/** Admin: create clubs, set the domains each club's site is served on, and
 *  choose who owns/manages each one. Managers see the club in the Manage
 *  section (account menu → Manage); owners can also change its managers. */
export default function ClubsAdminSection() {
  const [clubs, setClubs] = useState<ClubRow[] | null>(null);
  const [cnameTarget, setCnameTarget] = useState("");
  const [canProvision, setCanProvision] = useState(false);
  const [newName, setNewName] = useState("");
  const [newContact, setNewContact] = useState<ClubContact>({});
  const [managerEmail, setManagerEmail] = useState<Record<string, string>>({});
  const [managerRole, setManagerRole] = useState<Record<string, ClubRole>>({});
  const [domainsDraft, setDomainsDraft] = useState<Record<string, string>>({});
  const [contactDrafts, setContactDrafts] = useState<Record<string, ClubContact>>({});
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  function refresh() {
    listAdminClubs()
      .then((res) => {
        setClubs(res.clubs);
        setCnameTarget(res.cnameTarget ?? "");
        setCanProvision(!!res.canProvision);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load clubs."));
  }
  useEffect(refresh, []);

  async function run(action: () => Promise<string>) {
    setError(null);
    setInfo(null);
    try {
      setInfo(await action());
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    }
  }

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    run(async () => {
      const res = await createAdminClub(newName, newContact);
      setNewName("");
      setNewContact({});
      return `Created "${res.club.name}".`;
    });
  }

  function handleAddManager(e: React.FormEvent, club: ClubRow) {
    e.preventDefault();
    const email = managerEmail[club.clubId] ?? "";
    const role = managerRole[club.clubId] ?? "manager";
    run(async () => {
      await addClubManager(club.clubId, email, role);
      setManagerEmail({ ...managerEmail, [club.clubId]: "" });
      return `${email} is now ${role === "owner" ? "an owner" : "a manager"} of ${club.name}.`;
    });
  }

  function handleSaveDomains(e: React.FormEvent, club: ClubRow) {
    e.preventDefault();
    const domains = domainsDraft[club.clubId] ?? (club.domains ?? []).join("\n");
    run(async () => {
      const res = await updateAdminClub(club.clubId, { domains });
      const { [club.clubId]: _, ...rest } = domainsDraft;
      setDomainsDraft(rest);
      const list = res.club.domains ?? [];
      return list.length ? `${club.name} is served on ${list.join(", ")}.` : `${club.name} has no domains now.`;
    });
  }

  function handleSaveContact(e: React.FormEvent, club: ClubRow) {
    e.preventDefault();
    const contact = contactDrafts[club.clubId] ?? contactDraft(club.contact);
    run(async () => {
      await updateAdminClub(club.clubId, { contact });
      const { [club.clubId]: _, ...rest } = contactDrafts;
      setContactDrafts(rest);
      return `Saved ${club.name}'s contact info.`;
    });
  }

  function handleFacebookSignIn(club: ClubRow, facebookSignIn: boolean) {
    run(async () => {
      await updateAdminClub(club.clubId, { facebookSignIn });
      return facebookSignIn
        ? `Facebook sign-in is on for ${club.name}'s domains (Cognito picks it up within a few minutes).`
        : `Facebook sign-in is off for ${club.name}'s domains.`;
    });
  }

  function handleAdopt(club: ClubRow) {
    if (!confirm(`Give every event that has no club to "${club.name}"?`)) return;
    run(async () => {
      const res = await adoptClubEvents(club.clubId);
      return `${res.adopted} event${res.adopted === 1 ? "" : "s"} moved into ${club.name}.`;
    });
  }

  return (
    <div>
      <h2>Clubs</h2>
      {error && <p className="err">{error}</p>}
      {info && <Toast message={info} onDismiss={() => setInfo(null)} />}

      <form onSubmit={handleCreate}>
        <div className="row">
          <input placeholder="New club name" value={newName} onChange={(e) => setNewName(e.target.value)} required />
          <button type="submit" style={{ flex: "0 0 auto" }}>Create club</button>
        </div>
        <details style={{ marginTop: "0.5rem" }}>
          <summary className="muted" style={{ cursor: "pointer", fontSize: 13 }}>Contact info (optional)</summary>
          <div style={{ marginTop: "0.5rem" }}>
            <ClubContactFields idPrefix="new-club" value={newContact} onChange={setNewContact} />
          </div>
        </details>
      </form>

      {clubs?.map((club) => (
        <div key={club.clubId} className="card" style={{ marginTop: "0.75rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
            <h3 style={{ margin: 0, flex: 1 }}>{club.name}</h3>
            <button type="button" className="secondary" onClick={() => handleAdopt(club)}>
              Adopt unassigned events
            </button>
          </div>
          <form onSubmit={(e) => handleSaveDomains(e, club)} style={{ marginTop: "0.5rem" }}>
            <label className="muted" style={{ fontSize: 13 }}>
              Domains (one per line; the first is the primary). Visitors on these see only this club's
              events, uploads and Manage. Saving only records a domain; Provision sets up its
              certificate and CloudFront.
              <textarea
                rows={2}
                placeholder="rcccs-demo.sturgeon.tech"
                value={domainsDraft[club.clubId] ?? (club.domains ?? []).join("\n")}
                onChange={(e) => setDomainsDraft({ ...domainsDraft, [club.clubId]: e.target.value })}
              />
            </label>
            <CnameTarget target={cnameTarget} />
            {domainsDraft[club.clubId] !== undefined && (
              <button type="submit" style={{ marginTop: "0.25rem" }}>Save domains</button>
            )}
          </form>
          <DomainChecks
            clubId={club.clubId}
            domains={club.domains ?? []}
            lastResults={club.domainChecks}
            provisioning={club.domainProvisioning}
            records={club.domainRecords}
            canProvision={canProvision}
            onChanged={refresh}
          />
          <details style={{ margin: "0.5rem 0" }}>
            <summary className="muted" style={{ cursor: "pointer", fontSize: 13 }}>
              Contact info{club.contact?.email ? ` (${club.contact.email})` : " (none yet)"}
            </summary>
            <form onSubmit={(e) => handleSaveContact(e, club)} style={{ marginTop: "0.5rem" }}>
              <ClubContactFields
                idPrefix={`club-${club.clubId}`}
                value={contactDrafts[club.clubId] ?? contactDraft(club.contact)}
                onChange={(next) => setContactDrafts({ ...contactDrafts, [club.clubId]: next })}
              />
              <button type="submit" disabled={contactDrafts[club.clubId] === undefined}>Save contact info</button>
            </form>
          </details>
          <label className="muted" style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: 13 }}>
            <input
              type="checkbox"
              checked={club.facebookSignIn !== false}
              onChange={(e) => handleFacebookSignIn(club, e.target.checked)}
              style={{ width: "auto" }}
            />
            Allow Facebook sign-in on this club's domains
          </label>
          <p className="muted" style={{ fontSize: 13 }}>Owners and managers</p>
          {(club.managers ?? []).length === 0 && <p className="muted">No managers yet.</p>}
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {(club.managers ?? []).map((m) => (
              <li key={m.userId} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                {m.email} <span className="pill">{m.role ?? "manager"}</span>
                <button
                  type="button"
                  className="link"
                  title={`Remove ${m.email}`}
                  aria-label={`Remove ${m.email}`}
                  onClick={() =>
                    run(async () => {
                      await removeClubManager(club.clubId, m.userId);
                      return `${m.email} no longer manages ${club.name}.`;
                    })
                  }
                >
                  <Trash3FillIcon />
                </button>
              </li>
            ))}
          </ul>
          <form onSubmit={(e) => handleAddManager(e, club)} className="row" style={{ marginTop: "0.5rem" }}>
            <input
              type="email"
              placeholder="Manager's account email"
              value={managerEmail[club.clubId] ?? ""}
              onChange={(e) => setManagerEmail({ ...managerEmail, [club.clubId]: e.target.value })}
              required
            />
            <select
              value={managerRole[club.clubId] ?? "manager"}
              onChange={(e) => setManagerRole({ ...managerRole, [club.clubId]: e.target.value as ClubRole })}
              style={{ flex: "0 0 auto" }}
            >
              <option value="manager">Manager</option>
              <option value="owner">Owner</option>
            </select>
            <button type="submit" style={{ flex: "0 0 auto" }}>Add</button>
          </form>
        </div>
      ))}
    </div>
  );
}

/** Where a club points its domain: a CNAME to this stage's CloudFront
 *  hostname, or a Route53 alias for an apex domain. */
function CnameTarget({ target }: { target: string }) {
  const [copied, setCopied] = useState(false);
  if (!target) return null;
  return (
    <div className="muted" style={{ fontSize: 13, marginTop: "0.25rem" }}>
      Point each domain here (CNAME; apex domains use an alias / ANAME record):
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
        <code style={{ wordBreak: "break-all" }}>{target}</code>
        <button
          type="button"
          className="secondary"
          style={{ flex: "0 0 auto" }}
          onClick={() =>
            navigator.clipboard?.writeText(target).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            })
          }
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
}
